import { useState, FormEvent } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram } from "lucide-react";
import { BrandWordmark } from "@/components/brand/BrandWordmark";
import { supabase } from "@/integrations/supabase/client";

const Footer = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const aboutLinks = [
    { name: "About Us", href: "/about" },
    { name: "Our Mission", href: "/about#mission" },
    { name: "Blog", href: "/blog" },
    { name: "Community", href: "/community" },
    { name: "Sell With Us", href: "/vendor/register" },
  ];

  const categoryLinks = [
    { name: "Hijabs", href: "/hijabs" },
    { name: "Abayas", href: "/abayas" },
    { name: "Modest Dresses", href: "/modest-dresses" },
    { name: "Prayer Wear", href: "/prayer-wear" },
    { name: "Accessories", href: "/accessories" },
  ];

  const orderLinks = [
    { name: "My Orders", href: "/orders" },
    { name: "My Wishlist", href: "/wishlist" },
    { name: "My Account", href: "/account" },
    { name: "Shipping & Delivery", href: "/shipping" },
    { name: "Returns & Exchanges", href: "/returns" },
  ];

  const supportLinks = [
    { name: "Contact Us", href: "/contact" },
    { name: "FAQs", href: "/faq" },
    { name: "Size Guide", href: "/size-guide" },
    { name: "Privacy Policy", href: "/privacy" },
    { name: "Terms & Conditions", href: "/terms" },
  ];

  const PinterestIcon = ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/>
    </svg>
  );

  const TikTokIcon = ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
    </svg>
  );

  const socialLinks = [
    { name: "Facebook", icon: Facebook, href: "https://www.facebook.com/people/Hijabi-Store/61585983490843/" },
    { name: "Instagram", icon: Instagram, href: "https://www.instagram.com/hijabistore.official/" },
    { name: "Pinterest", icon: PinterestIcon, href: "https://www.pinterest.com/hijabistoreofficial/" },
    { name: "TikTok", icon: TikTokIcon, href: "https://www.tiktok.com/@hijabistoreofficial" },
  ];

  const handleNewsletterSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('submitting');

    try {
      let isDuplicate = false;
      const { error: insertError } = await supabase
        .from('newsletter_subscribers')
        .insert({ email: email.trim() });

      if (insertError) {
        if (insertError.code === '23505') {
          isDuplicate = true;
        } else {
          setStatus('error');
          return;
        }
      }

      await supabase.functions.invoke('send-email', {
        body: {
          type: 'early_access',
          to: email.trim(),
          subject: isDuplicate ? "You're already on the list..." : "You're on the list...",
          data: { isReturning: isDuplicate },
        },
      });

      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  // Mobile-friendly nav group: collapsible <details> on mobile, always-open block on md+
  const NavGroup = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <details className="group border-b border-white/10 md:border-0 [&[open]>div.mobile-only]:block">
      <summary className="flex items-center justify-between py-3 md:py-0 cursor-pointer md:cursor-default list-none [&::-webkit-details-marker]:hidden">
        <h3 className="font-semibold text-white text-sm md:mb-4">{title}</h3>
        <svg
          className="md:hidden h-4 w-4 text-white/60 transition-transform group-open:rotate-180"
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </summary>
      {/* Hidden on mobile when closed; always visible on md+ */}
      <div className="mobile-only hidden md:block pb-4 md:pb-0">{children}</div>
    </details>
  );

  // The dark ground comes from `fabric-texture-dark` (--fabric-base) rather than
  // a `bg-` class, so that colour and the veil it is composited with stay
  // defined together in one place.
  return (
    <footer className="fabric-texture fabric-texture-dark text-[hsl(40_10%_85%)]" role="contentinfo">
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-8 md:py-12">
        <div className="md:grid md:grid-cols-3 lg:grid-cols-5 md:gap-8 lg:gap-10">
          <NavGroup title="About HJB">
            <ul className="space-y-2.5">
              {aboutLinks.map((link) => (
                <li key={link.name}><Link to={link.href} className="text-white/60 hover:text-white transition-colors text-sm">{link.name}</Link></li>
              ))}
            </ul>
          </NavGroup>
          <NavGroup title="Shop by Category">
            <ul className="space-y-2.5">
              <li><Link to="/hijabs" className="text-white/60 hover:text-white transition-colors text-sm">Hijabs</Link></li>
              <li><Link to="/abayas" className="text-white/60 hover:text-white transition-colors text-sm">Abayas</Link></li>
              <li><Link to="/modest-dresses" className="text-white/60 hover:text-white transition-colors text-sm">Modest Dresses</Link></li>
              <li><Link to="/modest-activewear" className="text-white/60 hover:text-white transition-colors text-sm">Modest Activewear</Link></li>
              <li><Link to="/modest-swimwear" className="text-white/60 hover:text-white transition-colors text-sm">Modest Swimwear</Link></li>
              <li><Link to="/prayer-dresses" className="text-white/60 hover:text-white transition-colors text-sm">Prayer Dresses</Link></li>
              <li><Link to="/modest-workwear" className="text-white/60 hover:text-white transition-colors text-sm">Modest Workwear</Link></li>
            </ul>
          </NavGroup>
          <NavGroup title="More Styles">
            <ul className="space-y-2.5">
              <li><Link to="/modest-tops-tunics" className="text-white/60 hover:text-white transition-colors text-sm">Tops & Tunics</Link></li>
              <li><Link to="/modest-skirts" className="text-white/60 hover:text-white transition-colors text-sm">Modest Skirts</Link></li>
              <li><Link to="/jilbabs-khimars" className="text-white/60 hover:text-white transition-colors text-sm">Jilbabs & Khimars</Link></li>
              <li><Link to="/kaftans" className="text-white/60 hover:text-white transition-colors text-sm">Kaftans</Link></li>
              <li><Link to="/underscarves-hijab-caps" className="text-white/60 hover:text-white transition-colors text-sm">Underscarves & Caps</Link></li>
              <li><Link to="/modest-eveningwear" className="text-white/60 hover:text-white transition-colors text-sm">Evening Wear</Link></li>
              <li><Link to="/modest-cardigans-dusters" className="text-white/60 hover:text-white transition-colors text-sm">Cardigans & Dusters</Link></li>
            </ul>
          </NavGroup>
          <NavGroup title="Hijabs by Type">
            <ul className="space-y-2.5">
              <li><Link to="/chiffon-hijabs" className="text-white/60 hover:text-white transition-colors text-sm">Chiffon Hijabs</Link></li>
              <li><Link to="/jersey-hijabs" className="text-white/60 hover:text-white transition-colors text-sm">Jersey Hijabs</Link></li>
              <li><Link to="/silk-hijabs" className="text-white/60 hover:text-white transition-colors text-sm">Silk & Satin</Link></li>
              <li><Link to="/magnetic-hijabs" className="text-white/60 hover:text-white transition-colors text-sm">Magnetic Hijabs</Link></li>
              <li><Link to="/instant-hijabs" className="text-white/60 hover:text-white transition-colors text-sm">Instant / Pre-tied</Link></li>
              <li><Link to="/sports-hijabs-shop" className="text-white/60 hover:text-white transition-colors text-sm">Sports Hijabs</Link></li>
              <li><Link to="/hijab-pins-magnets-shop" className="text-white/60 hover:text-white transition-colors text-sm">Pins & Magnets</Link></li>
            </ul>
          </NavGroup>
          <NavGroup title="Abayas by Style">
            <ul className="space-y-2.5">
              <li><Link to="/open-front-abayas" className="text-white/60 hover:text-white transition-colors text-sm">Open-Front Abayas</Link></li>
              <li><Link to="/closed-abayas-shop" className="text-white/60 hover:text-white transition-colors text-sm">Closed Abayas</Link></li>
              <li><Link to="/kimono-abayas-shop" className="text-white/60 hover:text-white transition-colors text-sm">Kimono Abayas</Link></li>
              <li><Link to="/butterfly-abayas-shop" className="text-white/60 hover:text-white transition-colors text-sm">Butterfly Abayas</Link></li>
              <li><Link to="/embroidered-abayas-shop" className="text-white/60 hover:text-white transition-colors text-sm">Embroidered & Luxury</Link></li>
              <li><Link to="/eid-outfits" className="text-white/60 hover:text-white transition-colors text-sm">Eid Outfits</Link></li>
              <li><Link to="/modest-wedding-guest-dresses" className="text-white/60 hover:text-white transition-colors text-sm">Wedding Guest</Link></li>
            </ul>
          </NavGroup>
          <NavGroup title="Order & Purchases">
            <ul className="space-y-2.5">
              {orderLinks.map((link) => (
                <li key={link.name}><Link to={link.href} className="text-white/60 hover:text-white transition-colors text-sm">{link.name}</Link></li>
              ))}
            </ul>
          </NavGroup>
          <NavGroup title="Support & Services">
            <ul className="space-y-2.5">
              {supportLinks.map((link) => (
                <li key={link.name}><Link to={link.href} className="text-white/60 hover:text-white transition-colors text-sm">{link.name}</Link></li>
              ))}
            </ul>
          </NavGroup>
        </div>
      </div>

      {/* Newsletter Signup */}
      <div className="border-t border-white/10">
        <div className="container mx-auto px-4 py-10">
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <div>
              <h3 className="text-white font-semibold text-lg">Stay in the loop.</h3>
              <p className="text-white/60 text-sm mt-1">
                New brands, new arrivals, and your exclusive launch discount — straight to your inbox.
              </p>
            </div>
            <div>
              {status === 'success' ? (
                <p className="text-white/80 text-sm">You're on the list — your launch discount is coming soon.</p>
              ) : (
                <form onSubmit={handleNewsletterSubmit} className="space-y-2">
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="email"
                      required
                      inputMode="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Your email address"
                      className="flex-1 h-11 rounded-full bg-white/10 px-4 text-[15px] sm:text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-white/30"
                    />
                    <button
                      type="submit"
                      disabled={status === 'submitting'}
                      className="h-11 px-6 rounded-full bg-white text-[hsl(0_0%_12%)] text-[15px] sm:text-sm font-medium hover:brightness-90 transition-all disabled:opacity-60"
                    >
                      {status === 'submitting' ? 'Joining...' : 'Join the List'}
                    </button>
                  </div>
                  {status === 'error' && (
                    <p className="text-red-400 text-xs">Something went wrong — please try again.</p>
                  )}
                  <p className="text-white/40 text-xs">No spam. Unsubscribe anytime.</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3 text-sm text-white/40">
              <BrandWordmark tone="light" className="h-5" />
              <p className="text-white">© {new Date().getFullYear()} Hijabi Store | All Rights Reserved.</p>
            </div>

            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => { e.preventDefault(); window.open(social.href, "_blank", "noopener,noreferrer"); }}
                  className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                  aria-label={`Follow us on ${social.name}`}
                >
                  <social.icon className="h-4 w-4" />
                </a>
              ))}
            </div>

            <nav className="flex flex-wrap justify-center gap-4 text-xs text-white/40" aria-label="Legal navigation">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms & Conditions</Link>
              <Link to="/cookies" className="hover:text-white transition-colors">Cookie Policy</Link>
            </nav>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
